/**
 * 画面下部のトースト。操作結果・エラーの通知に使う。
 *
 * Popover API(popover="manual")でブラウザの**トップレイヤー**に表示する(2026-08-24)。
 * モーダルは <dialog>(トップレイヤー)で表示しているため、通常のz-indexでは
 * トーストがモーダルの裏に隠れてエラーが見えなくなる。後から showPopover した
 * 要素はトップレイヤー内でさらに前面に積まれるため、モーダルの上に必ず見える。
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';

type ToastContextValue = (message: string) => void;

const ToastContext = createContext<ToastContextValue | null>(null);

type PopoverDiv = HTMLDivElement & { showPopover?: () => void; hidePopover?: () => void };

export function ToastProvider({ children }: { children: ReactNode }) {
  const [message, setMessage] = useState('');
  const [visible, setVisible] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const boxRef = useRef<PopoverDiv | null>(null);

  const toast = useCallback((msg: string) => {
    setMessage(msg);
    setVisible(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setVisible(false), 3200);
  }, []);

  // 表示のたびに閉じて開き直す(モーダルより後に開くことでトップレイヤーの最前面に出す)
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    try {
      if (visible) {
        el.hidePopover?.();
        el.showPopover?.();
      } else {
        el.hidePopover?.();
      }
    } catch {
      // Popover API 非対応環境では何もしない(CSSのopacity切替のみで表示)
    }
  }, [visible, message]);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <div ref={boxRef} popover="manual" className={`toast${visible ? ' on' : ''}`} role="status" aria-live="polite">
        {message}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast は ToastProvider の内側で使ってください');
  return ctx;
}
